"use client";

import { isDev } from "@/lib/env";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { Suspense, useEffect } from "react";

interface PostHogUserData {
  id: string | null;
  email?: string;
  firstName?: string;
  lastName?: string;
  subscriptionTier?: string;
  isTrialActive?: boolean;
}

interface PostHogProviderProps {
  children: React.ReactNode;
  userData: PostHogUserData;
}

function PostHogUserIdentify({ userData }: { userData: PostHogUserData }) {
  const posthog = usePostHog();

  useEffect(() => {
    // Listen for logout events
    const handleLogout = () => {
      posthog.reset(true); // Reset both user and device ID
    };
    window.addEventListener("logout", handleLogout);

    function identifyUser() {
      if (!userData?.id) return;

      const properties = {
        email: userData.email,
        name: [userData.firstName, userData.lastName].filter(Boolean).join(" "),
        firstName: userData.firstName,
        lastName: userData.lastName,
        subscriptionTier: userData.subscriptionTier,
        isTrialActive: userData.isTrialActive,
        $initial_referrer: document.referrer,
        $initial_referring_domain: document.referrer
          ? new URL(document.referrer).hostname
          : null,
      };

      // Identify the user
      posthog.identify(userData.id, properties);

      // Group analytics handled separately in app layout where workspace context exists
    }

    if (posthog) {
      identifyUser();
    }

    return () => {
      window.removeEventListener("logout", handleLogout);
    };
  }, [posthog, userData]);

  return null;
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      const search = searchParams.toString();
      if (search) {
        url += `?${search}`;
      }
      posthog.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}

export function PostHogProvider({ children, userData }: PostHogProviderProps) {
  useEffect(() => {
    // don't track pageviews in dev
    if (!isDev) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
        api_host: "/ingest",
        ui_host: "https://us.posthog.com",
        capture_pageview: false, // We capture pageviews manually
        capture_pageleave: true, // Enable pageleave capture
        debug: process.env.NODE_ENV === "development",
        persistence: "localStorage", // Use localStorage for better persistence
        bootstrap: {
          distinctID: undefined, // Will be set by identify
          isIdentifiedID: false,
        },
      });
    }
  }, []);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      <Suspense fallback={null}>
        <PostHogUserIdentify userData={userData} />
      </Suspense>
      {children}
    </PHProvider>
  );
}
